import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/** Options a Call button can attach when opening the "pick who to call" dialog. */
export interface OpenCallDialogOptions {
  /**
   * Quick Call: restrict the dialog's roster to these user uids instead of the
   * whole team. Used by the transaction Details page to pre-filter down to the
   * users on that transaction — assigned via a step's RESOURCE field, or having
   * submitted a step. Omit (or pass an empty list) to keep the default full-team
   * roster.
   */
  restrictToUids?: string[];
}

/**
 * Controls the dashboard's outgoing-call UX:
 *  - `callDialogTxn` drives the "pick who to call" dialog (holds the transaction
 *    uid while open, null when closed).
 *  - `callDialogRestrictToUids` optionally narrows that dialog's roster (Quick
 *    Call); null means "no restriction — show every callable team member".
 *  - `outgoingCallId` is the legacy ringing-overlay channel, kept for the small
 *    OutgoingCallOverlay; the primary caller experience is now the full in-session
 *    call UI (the caller is navigated straight to /session and joins while ringing).
 */
interface OutgoingCallContextType {
  outgoingCallId: string | null;
  setOutgoingCallId: (id: string | null) => void;
  callDialogTxn: string | null;
  callDialogRestrictToUids: string[] | null;
  openCallDialog: (transactionUid: string, options?: OpenCallDialogOptions) => void;
  closeCallDialog: () => void;
}

const OutgoingCallContext = createContext<OutgoingCallContextType | null>(null);

export const OutgoingCallProvider = ({ children }: { children: ReactNode }) => {
  const [outgoingCallId, setOutgoingCallId] = useState<string | null>(null);
  const [callDialogTxn, setCallDialogTxn] = useState<string | null>(null);
  const [callDialogRestrictToUids, setCallDialogRestrictToUids] = useState<string[] | null>(null);

  const openCallDialog = useCallback((transactionUid: string, options?: OpenCallDialogOptions) => {
    const restrict = options?.restrictToUids?.filter(Boolean) || [];
    setCallDialogRestrictToUids(restrict.length > 0 ? restrict : null);
    setCallDialogTxn(transactionUid);
  }, []);
  const closeCallDialog = useCallback(() => {
    setCallDialogTxn(null);
    setCallDialogRestrictToUids(null);
  }, []);

  return (
    <OutgoingCallContext.Provider
      value={{
        outgoingCallId,
        setOutgoingCallId,
        callDialogTxn,
        callDialogRestrictToUids,
        openCallDialog,
        closeCallDialog
      }}
    >
      {children}
    </OutgoingCallContext.Provider>
  );
};

export const useOutgoingCall = (): OutgoingCallContextType => {
  const ctx = useContext(OutgoingCallContext);
  if (!ctx) throw new Error('useOutgoingCall must be used within an OutgoingCallProvider');
  return ctx;
};

export default OutgoingCallContext;
